/**
 * iManage's menu in its app area: Search, Recent, Favorites and Saved by
 * Amoreg; then the chosen library's workspaces with their client/matter
 * numbers; at the bottom, About and connection (the person's own connection,
 * with Disconnect) and, for admins, Settings.
 *
 * Disconnected, the area is its landing and has no sidebar, except on
 * Settings, where an admin sets iManage up: there the menu is a call to
 * connect instead.
 *
 * "Checked out to me" is deliberately absent: this client does not read
 * iManage check-outs.
 *
 * @module components/apps/imanage/IManageSidebarNav
 */
import { Link, useNavigate } from "@tanstack/react-router";
import {
  BriefcaseIcon,
  ChevronDownIcon,
  ClockIcon,
  CloudUploadIcon,
  InfoIcon,
  Loader2Icon,
  RotateCwIcon,
  SearchIcon,
  SettingsIcon,
  StarIcon,
} from "lucide-react";

import { cn } from "~/lib/utils";
import { useOrgProfile } from "../../../state/orgProfile";
import { Button } from "../../ui/button";
import { Menu, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuTrigger } from "../../ui/menu";
import { SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "../../ui/sidebar";
import { Skeleton } from "../../ui/skeleton";
import type { AppAreaNavProps } from "../AppsSidebar";
import { APP_AREA_ROW_CLASS, AppAreaNavGroup, AppAreaNavRow } from "../appAreaNavParts";
import { useIManageConnect } from "./imanageAreaContext";
import { useIManageList } from "./imanageLocalLists";
import { imanageSection, libraryOfId } from "./imanagePaths";
import { invalidateIManage, useIManageLibrary, useIManageStatusQuery, useWorkspaces } from "./imanageQueries";
import { MatterNumber } from "./IManageParts";

const SIDEBAR_WORKSPACE_LIMIT = 30;

const ROWS = {
  search: "/$org/apps/imanage/search",
  recent: "/$org/apps/imanage/recent",
  favorites: "/$org/apps/imanage/favorites",
  saved: "/$org/apps/imanage/saved",
} as const;

export function IManageSidebarNav({ orgSegment, organizationId, pathname, onNavigate }: AppAreaNavProps) {
  const status = useIManageStatusQuery(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  const connected = status.data?.connected === true;
  const section = imanageSection(pathname);
  const [favorites] = useIManageList(organizationId, "favorites");
  const saved = status.data?.saves.filter((s) => s.status === "saved").length ?? 0;

  const row = (
    key: keyof typeof ROWS,
    label: string,
    icon: React.ReactNode,
    count?: number,
  ) => (
    <AppAreaNavRow
      key={key}
      data-app-area-row={key}
      active={section?.kind === key}
      icon={icon}
      label={label}
      {...(count !== undefined ? { count } : {})}
      render={<Link to={ROWS[key]} params={{ org: orgSegment }} onClick={onNavigate} />}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-imanage-sidebar="">
      {connected ? (
        <>
          <AppAreaNavGroup>
            {row("search", "Search iManage", <SearchIcon />)}
            {row("recent", "Recent", <ClockIcon />)}
            {row("favorites", "Favorites", <StarIcon />, favorites.length)}
            {row("saved", "Saved by Amoreg", <CloudUploadIcon />, saved)}
          </AppAreaNavGroup>
          <Workspaces
            organizationId={organizationId}
            orgSegment={orgSegment}
            activeWorkspace={section?.kind === "workspace" ? section.workspaceId : null}
            homeActive={section?.kind === "home"}
            onNavigate={onNavigate}
          />
        </>
      ) : (
        <ConnectCard
          organizationId={organizationId}
          orgSegment={orgSegment}
          loading={status.loading}
          configured={status.data?.configured ?? false}
          pending={status.data?.pending ?? false}
          canManage={canManage}
          onNavigate={onNavigate}
        />
      )}
      <div className="mt-auto pb-1">
        <AppAreaNavGroup>
          {connected ? (
            <AppAreaNavRow
              data-app-area-row="about"
              active={section?.kind === "about"}
              icon={<InfoIcon />}
              label="About and connection"
              muted={section?.kind !== "about"}
              render={<Link to="/$org/apps/imanage/about" params={{ org: orgSegment }} onClick={onNavigate} />}
            />
          ) : null}
          {canManage ? (
            <AppAreaNavRow
              data-app-area-row="settings"
              active={section?.kind === "settings"}
              icon={<SettingsIcon />}
              label="Settings"
              muted={section?.kind !== "settings"}
              render={<Link to="/$org/apps/imanage/settings" params={{ org: orgSegment }} onClick={onNavigate} />}
            />
          ) : null}
        </AppAreaNavGroup>
      </div>
    </div>
  );
}

function Workspaces({
  organizationId,
  orgSegment,
  activeWorkspace,
  homeActive,
  onNavigate,
}: {
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly activeWorkspace: string | null;
  readonly homeActive: boolean;
  readonly onNavigate: () => void;
}) {
  const { library, libraries, choose } = useIManageLibrary(
    organizationId,
    true,
    activeWorkspace ? libraryOfId(activeWorkspace) : null,
  );
  const workspaces = useWorkspaces(organizationId, library);
  const navigate = useNavigate();
  const offered = libraries.data?.entries ?? [];
  const current = offered.find((l) => l.id === library);
  const entries = workspaces.data?.entries ?? [];
  const shown = entries.slice(0, SIDEBAR_WORKSPACE_LIMIT);
  const more = entries.length > shown.length || workspaces.data?.nextOffset != null;

  return (
    <div className="flex flex-col">
      <div className="flex items-center pe-1">
        <SidebarGroupLabel className="h-7 flex-1 text-sidebar-muted-foreground/70">Workspaces</SidebarGroupLabel>
        {offered.length > 1 && library ? (
          <Menu>
            <MenuTrigger
              aria-label={`Library: ${current?.name ?? library}`}
              className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-xs text-sidebar-muted-foreground outline-none transition-colors hover:bg-sidebar-row-hover hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-ring data-[popup-open]:bg-sidebar-row-hover"
              data-imanage-library-menu=""
            >
              {current?.name ?? library}
              <ChevronDownIcon aria-hidden className="size-3" />
            </MenuTrigger>
            <MenuPopup align="end" className="min-w-40">
              <MenuRadioGroup value={library} onValueChange={(value) => {
                  choose(String(value));
                  onNavigate();
                  void navigate({
                    to: "/$org/apps/imanage",
                    params: { org: orgSegment },
                    search: { lib: String(value) },
                  });
                }}
              >
                {offered.map((l) => (
                  <MenuRadioItem key={l.id} value={l.id} closeOnClick>
                    {l.name}
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>
            </MenuPopup>
          </Menu>
        ) : null}
      </div>
      <SidebarMenu className="ps-px">
        {workspaces.loading || libraries.loading ? (
          Array.from({ length: 4 }, (_, i) => (
            <SidebarMenuItem key={i}>
              <div className={cn(APP_AREA_ROW_CLASS, "flex items-center")}>
                <Skeleton className="size-4 rounded" />
                <Skeleton className="h-3" style={{ width: `${60 - i * 9}%` }} />
              </div>
            </SidebarMenuItem>
          ))
        ) : workspaces.error && !workspaces.data ? (
          <SidebarMenuItem>
            <button
              type="button"
              onClick={() => invalidateIManage(organizationId, ["libraries", "browse"])}
              className={cn(
                APP_AREA_ROW_CLASS,
                "flex w-full items-center rounded-md text-left text-[13px] text-sidebar-muted-foreground hover:bg-sidebar-row-hover",
              )}
            >
              <RotateCwIcon aria-hidden className="size-4" />
              <span className="truncate">Workspaces did not load. Retry</span>
            </button>
          </SidebarMenuItem>
        ) : (
          <>
            {shown.map((w) => (
              <SidebarMenuItem key={w.id}>
                <SidebarMenuButton
                  className={APP_AREA_ROW_CLASS}
                  isActive={w.id === activeWorkspace}
                  data-imanage-workspace-row={w.id}
                  render={
                    <Link
                      to="/$org/apps/imanage/w/$workspaceId"
                      params={{ org: orgSegment, workspaceId: w.id }}
                      onClick={onNavigate}
                      title={[w.client && w.matter ? `${w.client}-${w.matter}` : w.client, w.name, w.clientName]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                  }
                >
                  <BriefcaseIcon />
                  <span className="min-w-0 flex-1 truncate">{w.name}</span>
                  <MatterNumber
                    entry={w.client ? { client: w.client } : {}}
                    className="text-[10.5px] text-sidebar-muted-foreground/80"
                  />
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
            <SidebarMenuItem>
              <SidebarMenuButton
                className={cn(APP_AREA_ROW_CLASS, !homeActive && "text-sidebar-muted-foreground/80")}
                isActive={homeActive}
                data-app-area-row="home"
                render={
                  <Link
                    to="/$org/apps/imanage"
                    params={{ org: orgSegment }}
                    {...(library ? { search: { lib: library } } : {})}
                    onClick={onNavigate}
                  />
                }
              >
                <span aria-hidden className="size-4" />
                <span className="truncate">{more ? `All ${entries.length}+ workspaces` : "All workspaces"}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </>
        )}
      </SidebarMenu>
    </div>
  );
}

function ConnectCard({
  organizationId,
  orgSegment,
  loading,
  configured,
  pending,
  canManage,
  onNavigate,
}: {
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly loading: boolean;
  readonly configured: boolean;
  readonly pending: boolean;
  readonly canManage: boolean;
  readonly onNavigate: () => void;
}) {
  const { connect, busy } = useIManageConnect(organizationId, () =>
    invalidateIManage(organizationId, ["status"]),
  );
  if (loading)
    return (
      <div className="flex flex-col gap-2 px-2.5">
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    );
  return (
    <div
      className="mx-1 flex flex-col gap-2.5 rounded-lg border border-sidebar-border bg-sidebar-control-surface/60 p-3 text-[13px]"
      data-imanage-connect-card=""
    >
      <p className="font-medium text-sidebar-foreground">
        {!configured ? "iManage is not set up" : pending ? "Finish signing in" : "Connect iManage"}
      </p>
      <p className="text-xs leading-relaxed text-sidebar-muted-foreground">
        {!configured
          ? canManage
            ? "Add your firm's iManage application in Settings."
            : "Ask an organization admin to set up iManage."
          : pending
            ? "Complete the sign-in in the iManage tab."
            : "Sign in with your own iManage account to browse workspaces here."}
      </p>
      {configured ? (
        <Button
          size="xs"
          variant={pending ? "outline" : "default"}
          className="self-start"
          onClick={connect}
          disabled={busy}
        >
          {busy ? <Loader2Icon aria-hidden className="animate-spin" /> : null}
          {pending ? "Sign in again" : "Connect"}
        </Button>
      ) : canManage ? (
        <Button
          size="xs"
          variant="outline"
          className="self-start"
          render={<Link to="/$org/apps/imanage/settings" params={{ org: orgSegment }} onClick={onNavigate} />}
        >
          Open settings
        </Button>
      ) : null}
    </div>
  );
}
